import {Component, Inject, ChangeDetectionStrategy} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {
  WaButtonDirective,
  WaDialogDirective,
  WaDividerDirective,
  WaScrollerDirective,
  WaToastService
} from "angular-awesome";
import {DIALOG_DATA, DialogRef} from "@angular/cdk/dialog";
import {Survey} from "../../../api/models/survey";
import {StaffMember} from "../../../api/models/staff-member";
import {Exam} from "../../../api/models/exam";
import {ExamCorrectionAssignmentsData} from "../../../api/models/exam-assignments";
import {CorrectionStaffMemberComponent} from "./correction-staff-member/correction-staff-member.component";
import {
  CorrectionFixedStaffMemberComponent
} from "./correction-fixed-staff-member/correction-fixed-staff-member.component";
import {
  CorrectionExcludedStaffMemberComponent
} from "./correction-excluded-staff-member/correction-excluded-staff-member.component";


@Component({
  selector: 'app-exam-assignments-correction-dialog',
  imports: [ReactiveFormsModule, WaDialogDirective, WaButtonDirective, WaDividerDirective, CorrectionStaffMemberComponent, CorrectionFixedStaffMemberComponent, CorrectionExcludedStaffMemberComponent, WaScrollerDirective],
  templateUrl: './exam-assignments-correction-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './exam-assignments-correction-dialog.component.scss',
})
export class ExamAssignmentsCorrectionDialogComponent {
  examAssignmentsForm = new FormGroup({});

  //private isLoading: boolean = true;
  //private lockAcquired: boolean = false;

  survey: Survey | null = null;
  exam: Exam | null = null;
  staffMembers: StaffMember[] = [];
  fixedCorrectors: StaffMember[] = [];
  suggestedCorrectors: StaffMember[] = [];
  excludedCorrectors: StaffMember[] = [];


  constructor(private dialogRef: DialogRef<ExamCorrectionAssignmentsData>, private toastService: WaToastService, @Inject(DIALOG_DATA) data: ExamCorrectionAssignmentsData) {
    if (!data) {
      return;
    }
    if (!data?.survey) {
      return;
    }
    if (!data?.exam) {
      return;
    }
    this.survey = data.survey;
    this.exam = data.exam;
    //use clones of input arrays
    this.staffMembers = [...data.staffMembers].filter(this.canCorrect).sort(this.compareStaffMembers);
    this.fixedCorrectors = [...data.fixedCorrectors].sort(this.compareStaffMembers);
    this.suggestedCorrectors = [...data.suggestedCorrectors].sort(this.compareStaffMembers);
    this.excludedCorrectors = [...data.excludedCorrectors].sort(this.compareStaffMembers);
    // exclude elements that are already listed in fixedCorrectors, suggestedCorrectors or excludedCorrectors
    const excludedIds = new Set<number>([
      ...this.fixedCorrectors.map(s => s.id),
      ...this.suggestedCorrectors.map(s => s.id),
      ...this.excludedCorrectors.map(s => s.id)
    ]);
    this.staffMembers = this.staffMembers.filter(s => !excludedIds.has(s.id));
  }

  compareStaffMembers(a: StaffMember, b: StaffMember) {
    if (a.last_name === b.last_name) {
      return a.first_name.localeCompare(b.first_name);
    } else {
      return a.last_name.localeCompare(b.last_name);
    }
  }

  isValid(): boolean {
    return (!!this.survey && !!this.exam);
  }

  apply(): void {
    this.dialogRef.close({
      survey: this.survey,
      exam: this.exam,
      staffMembers: this.staffMembers,
      fixedCorrectors: this.fixedCorrectors,
      suggestedCorrectors: this.suggestedCorrectors,
      excludedCorrectors: this.excludedCorrectors
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  sortCorrectorsLists() {
    this.staffMembers.sort(this.compareStaffMembers);
    this.fixedCorrectors.sort(this.compareStaffMembers);
    this.suggestedCorrectors.sort(this.compareStaffMembers);
    this.excludedCorrectors.sort(this.compareStaffMembers);
  }

  setFixedStaffMember(staffmember: StaffMember): void {
    this.fixedCorrectors.push(staffmember);
    this.suggestedCorrectors = this.suggestedCorrectors.filter(s => s.id !== staffmember.id);
    this.staffMembers = this.staffMembers.filter(s => s.id !== staffmember.id);
    this.excludedCorrectors = this.excludedCorrectors.filter(s => s.id !== staffmember.id);
    this.sortCorrectorsLists();
  }

  setRegularStaffMember(staffmember: StaffMember): void {
    this.staffMembers.push(staffmember);
    this.fixedCorrectors = this.fixedCorrectors.filter(s => s.id !== staffmember.id);
    this.suggestedCorrectors = this.suggestedCorrectors.filter(s => s.id !== staffmember.id);
    this.excludedCorrectors = this.excludedCorrectors.filter(s => s.id !== staffmember.id);
    this.sortCorrectorsLists();
  }

  setSuggestedStaffMember(staffmember: StaffMember): void {
    this.suggestedCorrectors.push(staffmember);
    this.suggestedCorrectors.sort(this.compareStaffMembers);
    this.fixedCorrectors = this.fixedCorrectors.filter(s => s.id !== staffmember.id);
    this.staffMembers = this.staffMembers.filter(s => s.id !== staffmember.id);
    this.excludedCorrectors = this.excludedCorrectors.filter(s => s.id !== staffmember.id);
    this.sortCorrectorsLists();
  }

  setExcludedStaffMember(staffmember: StaffMember): void {
    this.excludedCorrectors.push(staffmember);
    this.excludedCorrectors.sort(this.compareStaffMembers);
    this.fixedCorrectors = this.fixedCorrectors.filter(s => s.id !== staffmember.id);
    this.suggestedCorrectors = this.suggestedCorrectors.filter(s => s.id !== staffmember.id);
    this.staffMembers = this.staffMembers.filter(s => s.id !== staffmember.id);
    this.sortCorrectorsLists();

  }

  canCorrect(staffmember: StaffMember): boolean {
    return staffmember.required_corrections > 0;
  }
}


